import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store'
import { fetchBalance } from '../../store/slices/walletSlice'
import { walletApi } from '../../api/walletApi'
import { WalletTransaction } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, ArrowDownLeft, ArrowUpRight, IndianRupee } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { clsx } from 'clsx'

declare global {
  interface Window {
    Razorpay: any
  }
}

const AMOUNT_PRESETS = [100, 200, 500, 1000, 2000]

const TX_TYPE_LABEL: Record<string, string> = {
  TOP_UP: 'Added funds',
  REFERRAL_FEE_DEDUCT: 'Referral fee',
  ESCROW_HOLD: 'Escrow hold',
  ESCROW_RELEASE: 'Referral earned',
  ESCROW_REFUND: 'Refund received',
  WITHDRAWAL: 'Withdrawal',
  REFUND: 'Refund',
  PROMOTIONAL_CREDIT: 'Promo credit',
}

const TX_COLOR: Record<string, string> = {
  TOP_UP: 'text-green-600',
  ESCROW_RELEASE: 'text-green-600',
  ESCROW_REFUND: 'text-green-600',
  PROMOTIONAL_CREDIT: 'text-green-600',
  REFUND: 'text-green-600',
  REFERRAL_FEE_DEDUCT: 'text-red-500',
  ESCROW_HOLD: 'text-orange-500',
  WITHDRAWAL: 'text-red-500',
}

const TX_CREDIT = new Set(['TOP_UP', 'ESCROW_RELEASE', 'ESCROW_REFUND', 'PROMOTIONAL_CREDIT', 'REFUND'])

export default function WalletPage() {
  const dispatch = useAppDispatch()
  const { balance, loading: balanceLoading } = useAppSelector((s) => s.wallet)
  const [transactions, setTransactions] = useState<WalletTransaction[]>([])
  const [txLoading, setTxLoading] = useState(true)
  const [addFundsAmount, setAddFundsAmount] = useState(500)
  const [addFundsLoading, setAddFundsLoading] = useState(false)
  const [tab, setTab] = useState<'overview' | 'transactions'>('overview')

  useEffect(() => {
    dispatch(fetchBalance())
    walletApi
      .getTransactions()
      .then(({ data }) => setTransactions(data.data?.content || []))
      .catch(() => toast.error('Failed to load transactions'))
      .finally(() => setTxLoading(false))
  }, [dispatch])

  const handleAddFunds = async () => {
    setAddFundsLoading(true)
    try {
      // Dynamically load Razorpay SDK
      if (!window.Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script')
          script.src = 'https://checkout.razorpay.com/v1/checkout.js'
          script.onload = () => resolve()
          script.onerror = () => reject(new Error('Failed to load Razorpay'))
          document.body.appendChild(script)
        })
      }

      const { data } = await walletApi.createRazorpayOrder(addFundsAmount * 100)
      const order = data.data!

      const rzp = new window.Razorpay({
        key: order.razorpayKeyId,
        amount: order.amount,
        currency: 'INR',
        name: 'Referrals',
        description: 'Add funds to wallet',
        order_id: order.razorpayOrderId,
        handler: async (response: any) => {
          try {
            await walletApi.verifyRazorpayPayment(response)
            toast.success(`₹${addFundsAmount} added to your wallet!`)
            dispatch(fetchBalance())
            walletApi.getTransactions().then(({ data }) => setTransactions(data.data?.content || []))
          } catch {
            toast.error('Payment verification failed')
          }
        },
        prefill: {},
        theme: { color: '#6366f1' },
      })
      rzp.open()
    } catch (err: any) {
      toast.error(err.message || 'Failed to initiate payment')
    } finally {
      setAddFundsLoading(false)
    }
  }

  const balanceRupees = (balance || 0) / 100

  return (
    <div className="max-w-2xl mx-auto">
      {/* Balance card */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 text-white mb-6">
        <p className="text-primary-200 text-sm">Available Balance</p>
        <div className="flex items-baseline gap-1 mt-1">
          <IndianRupee size={28} />
          <span className="text-4xl font-bold">
            {balanceLoading ? '...' : balanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {(['overview', 'transactions'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              tab === t ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card">
          <h2 className="font-semibold text-gray-900 mb-4">Add Funds</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {AMOUNT_PRESETS.map((amount) => (
              <button
                key={amount}
                onClick={() => setAddFundsAmount(amount)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  addFundsAmount === amount
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300'
                }`}
              >
                ₹{amount}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
              <input
                type="number"
                value={addFundsAmount}
                onChange={(e) => setAddFundsAmount(Number(e.target.value))}
                className="input pl-7"
                min={1}
              />
            </div>
            <button onClick={handleAddFunds} disabled={addFundsLoading} className="btn-primary flex items-center gap-2">
              {addFundsLoading ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownLeft size={16} />}
              Add Money
            </button>
          </div>
        </div>
      )}

      {tab === 'transactions' && (
        <div>
          {txLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 size={24} className="animate-spin text-primary-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="card text-center py-12 text-gray-400">No transactions yet</div>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-9 h-9 rounded-full flex items-center justify-center', TX_CREDIT.has(tx.type) ? 'bg-green-50' : 'bg-red-50')}>
                      {TX_CREDIT.has(tx.type) ? (
                        <ArrowDownLeft size={16} className="text-green-600" />
                      ) : (
                        <ArrowUpRight size={16} className="text-red-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {TX_TYPE_LABEL[tx.type] || tx.type}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(tx.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={clsx('font-semibold', TX_COLOR[tx.type] || 'text-gray-700')}>
                      {TX_CREDIT.has(tx.type) ? '+' : '-'}₹{tx.amountPaise / 100}
                    </p>
                    <p className="text-xs text-gray-400">Bal: ₹{tx.balanceAfterPaise / 100}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
